import { calculateIMC, classifyIMC, classifyBP, classifyGlucose, calculateAge } from "@/pages/FormF1/helpers";
import { DEFAULT_THRESHOLDS } from "@/services/alertsService";

// ── Alert Generation ─────────────────────────────────────────────────────────
/**
 * Genera alertas a partir del perfil del usuario.
 * Acepta umbrales personalizados (desde Firestore) o usa los defaults clínicos.
 * Retorna array de { variant: 'critical'|'danger'|'warning'|'info', text, type, value, threshold }
 */
export function generateAlerts(profile, thresholds = DEFAULT_THRESHOLDS, omsConfig = null) {
  const alerts = [];
  const t = thresholds;

  const hasF1 = !!profile.completedAt;
  const hasF2 = !!profile.f2Results?.completedAt;
  const hasF3 = !!profile.f3Results?.completedAt;
  const hasF4 = !!profile.f4Results?.completedAt;
  const hasF5 = !!profile.f5Results?.completedAt;

  if (hasF1) {
    // ── IMC ────────────────────────────────────────────────────────────────
    const imc = calculateIMC(profile.peso, profile.talla);
    const imcCls = classifyIMC(imc);
    if (imc >= (t.imc?.critical ?? 40))
      alerts.push({ variant: "critical", level: 3, type: "imc", value: imc, threshold: t.imc?.critical, text: `IMC: ${imc} — Obesidad Mórbida. Sugerencia de consulta con Médico General de forma prioritaria.` });
    else if (imc >= (t.imc?.danger ?? 30))
      alerts.push({ variant: "danger", level: 2, type: "imc", value: imc, threshold: t.imc?.danger, text: `IMC: ${imc} — ${imcCls?.label ?? "Obesidad"}. Sugerencia de consulta con Médico General.` });
    else if (imc >= (t.imc?.warning ?? 25))
      alerts.push({ variant: "warning", level: 1, type: "imc", value: imc, threshold: t.imc?.warning, text: `IMC: ${imc} — Sobrepeso. Seguimiento con Oficial de Salud (IHO).` });
    else if (imc < 18.5 && imc > 0)
      alerts.push({ variant: "warning", level: 1, type: "imc", value: imc, text: `IMC: ${imc} — Bajo peso. Seguimiento con Oficial de Salud (IHO).` });

    // ── Relación Cintura-Altura (WHtR > 0.5) ──────────────────────────────
    const cin = parseFloat(profile.cintura);
    const tal = parseFloat(profile.talla);
    if (!isNaN(cin) && !isNaN(tal) && tal > 0) {
      const whtr = cin / tal;
      if (whtr > 0.5) {
        alerts.push({
          variant: "warning",
          level: 2,
          type: "whtr",
          value: parseFloat(whtr.toFixed(2)),
          threshold: 0.5,
          text: `Relación Cintura-Altura: ${whtr.toFixed(2)} — Factor de riesgo cardiometabólico (>0.5). Sugerencia de consulta con Médico General.`
        });
      }
    }

    // ── Presión Arterial ───────────────────────────────────────────────────
    const pas = parseFloat(profile.paSistolica);
    const pad = parseFloat(profile.paDiastolica);
    if (!isNaN(pas) && !isNaN(pad)) {
      if (pas >= (t.paSistolica?.critical ?? 180) || pad >= (t.paDiastolica?.critical ?? 120))
        alerts.push({ variant: "critical", level: 3, type: "pa", value: `${pas}/${pad}`, threshold: `${t.paSistolica?.critical}/${t.paDiastolica?.critical}`, text: `Presión arterial: ${pas}/${pad} mmHg — Urgencia hipertensiva. Sugerencia de valoración médica inmediata con Médico General.` });
      else if (pas >= (t.paSistolica?.danger ?? 140) || pad >= (t.paDiastolica?.danger ?? 90))
        alerts.push({ variant: "danger", level: 2, type: "pa", value: `${pas}/${pad}`, threshold: `${t.paSistolica?.danger}/${t.paDiastolica?.danger}`, text: `Presión arterial: ${pas}/${pad} mmHg — HTA Estadio II. Sugerencia de consulta con Médico General.` });
      else if (pas >= (t.paSistolica?.warning ?? 130) || pad >= (t.paDiastolica?.warning ?? 80))
        alerts.push({ variant: "warning", level: 1, type: "pa", value: `${pas}/${pad}`, text: `Presión arterial: ${pas}/${pad} mmHg — HTA Estadio I. Monitoreo con Oficial de Salud (IHO).` });
      else if (pas >= (t.paSistolica?.info ?? 120))
        alerts.push({ variant: "info", level: 0, type: "pa", value: pas, text: `Presión arterial: ${pas}/${pad} mmHg — Elevada (revisar hábitos).` });
    }

    // ── Glucemia ───────────────────────────────────────────────────────────
    const glu = parseFloat(profile.glucemia);
    if (!isNaN(glu)) {
      if (glu < (t.glucemia?.hypoCritical ?? 54))
        alerts.push({ variant: "critical", level: 3, type: "glucemia", value: glu, threshold: t.glucemia?.hypoCritical, text: `Glucemia: ${glu} mg/dl — Hipoglucemia severa. Sugerencia de atención médica urgente.` });
      else if (glu < (t.glucemia?.hypo ?? 70))
        alerts.push({ variant: "danger", level: 2, type: "glucemia", value: glu, threshold: t.glucemia?.hypo, text: `Glucemia: ${glu} mg/dl — Hipoglucemia. Sugerencia de consulta con Médico General.` });
      else if (glu > (t.glucemia?.severe ?? 250))
        alerts.push({ variant: "critical", level: 3, type: "glucemia", value: glu, threshold: t.glucemia?.severe, text: `Glucemia: ${glu} mg/dl — Hiperglucemia severa. Sugerencia de atención médica urgente.` });
      else if (glu >= (t.glucemia?.danger ?? 126))
        alerts.push({ variant: "danger", level: 2, type: "glucemia", value: glu, threshold: t.glucemia?.danger, text: `Glucemia: ${glu} mg/dl — Posible diabetes. Sugerencia de consulta con Médico General.` });
      else if (glu >= (t.glucemia?.warning ?? 100))
        alerts.push({ variant: "warning", level: 1, type: "glucemia", value: glu, text: `Glucemia: ${glu} mg/dl — Prediabetes. Monitoreo con Oficial de Salud (IHO).` });
    }

    // ── Oximetría ──────────────────────────────────────────────────────────
    const oxi = parseFloat(profile.oximetria);
    if (!isNaN(oxi)) {
      if (oxi < (t.oximetria?.critical ?? 90))
        alerts.push({ variant: "critical", level: 3, type: "oximetria", value: oxi, threshold: t.oximetria?.critical, text: `Oximetría: ${oxi}% — Hipoxemia grave. Sugerencia de atención médica urgente.` });
      else if (oxi < (t.oximetria?.danger ?? 92))
        alerts.push({ variant: "danger", level: 2, type: "oximetria", value: oxi, threshold: t.oximetria?.danger, text: `Oximetría: ${oxi}% — Hipoxemia. Sugerencia de consulta prioritaria con Médico General.` });
      else if (oxi < (t.oximetria?.warning ?? 95))
        alerts.push({ variant: "warning", level: 1, type: "oximetria", value: oxi, text: `Oximetría: ${oxi}% — Por debajo de lo normal. Monitoreo con Oficial de Salud (IHO).` });
    }

    // ── Sueño ──────────────────────────────────────────────────────────────
    const sueno = parseFloat(profile.sueno);
    if (!isNaN(sueno) && sueno < (t.sueno?.warning ?? 7))
      alerts.push({ variant: "warning", level: 1, type: "sueno", value: sueno, text: `Sueño: ${sueno}h — Menos de 7h recomendadas. Acompañamiento con Oficial de Salud (IHO).` });
  }

  // ── Clasificación Oficial Google Healthcare API (FHIR) ───────────────────
  if (profile.fhirRiskClassification === "enfermo") {
    alerts.push({
      variant: "danger",
      level: 2,
      type: "fhir_risk",
      text: "Clasificación clínica FHIR (GCP): Patología activa identificada. Sugerencia de consulta con Médico General."
    });
  } else if (profile.fhirRiskClassification === "con_factores_de_riesgo") {
    alerts.push({
      variant: "warning",
      level: 1,
      type: "fhir_risk",
      text: "Clasificación clínica FHIR (GCP): Factores de riesgo activos. Monitoreo preventivo con Oficial de Salud (IHO)."
    });
  }

  // ── PAR-Q ────────────────────────────────────────────────────────────────
  // Solo se muestra como alerta de atención si AÚN no se resolvió el
  // consentimiento médico. Una vez subido el documento o aceptado el riesgo,
  // deja de aparecer (el estado queda visible en Mi Cuenta).
  if (hasF2 && profile.f2Results.parqAlerta && !profile.parqConsentimiento)
    alerts.push({ variant: "danger", level: 2, type: "parq", text: `PAR-Q: ${profile.f2Results.parqRiskCount} ${profile.f2Results.parqRiskCount === 1 ? "riesgo detectado" : "riesgos detectados"}. Sugerencia de valoración con Médico General.` });

  // ── IPAQ / Sedentarismo ──────────────────────────────────────────────────
  if (hasF3) {
    if (profile.f3Results.clasificacionIPAQ === "low")
      alerts.push({ variant: "danger", level: 1, type: "ipaq", text: `Actividad física: IPAQ Bajo — ${profile.f3Results.metTotal} MET-min/sem. Seguimiento con Oficial de Salud (IHO).` });
    const sed = profile.f3Results.indiceSedentarismo;
    if (sed === "very_high")
      alerts.push({ variant: "warning", level: 1, type: "sedentarismo", text: "Sedentarismo: Muy alto. Seguimiento con Oficial de Salud (IHO)." });
    else if (sed === "high")
      alerts.push({ variant: "warning", level: 1, type: "sedentarismo", text: "Sedentarismo: Alto. Seguimiento con Oficial de Salud (IHO)." });
  }

  // ── Dieta / Conductas ────────────────────────────────────────────────────
  if (hasF4) {
    if (profile.f4Results.categoriaCalidadDieta === "poor")
      alerts.push({ variant: "warning", level: 1, type: "dieta", text: `Dieta: ${profile.f4Results.scoreCalidadDieta}/40 — Deficiente. Acompañamiento nutricional (IHO).` });
    else if (profile.f4Results.categoriaCalidadDieta === "fair")
      alerts.push({ variant: "warning", level: 1, type: "dieta", text: `Dieta: ${profile.f4Results.scoreCalidadDieta}/40 — Mejorable. Recomendación de hábitos saludables.` });
    if (profile.f4Results.categoriaConductas === "risk")
      alerts.push({ variant: "warning", level: 1, type: "conductas", text: `Conductas alimentarias: ${profile.f4Results.scoreConductas}/15 — De riesgo. Acompañamiento con Oficial de Salud (IHO).` });
  }

  // ── Emocional ────────────────────────────────────────────────────────────
  if (hasF5) {
    const g = profile.f5Results.categoriaGlobal;
    if (g === "critical")
      alerts.push({ variant: "danger", level: 2, type: "emocional", text: `Score emocional: ${profile.f5Results.scoreGlobal}/100 — Crítico. Sugerencia de consulta con Médico General.` });
    else if (g === "low")
      alerts.push({ variant: "danger", level: 2, type: "emocional", text: `Score emocional: ${profile.f5Results.scoreGlobal}/100 — Bajo. Sugerencia de consulta con Médico General.` });

    const a = profile.f5Results.ansiedad?.categoria;
    if (a === "severe")
      alerts.push({ variant: "danger", level: 2, type: "ansiedad", text: `Ansiedad: ${profile.f5Results.ansiedad.score}/15 — Severa. Sugerencia de consulta con Médico General.` });
    else if (a === "moderate")
      alerts.push({ variant: "danger", level: 1, type: "ansiedad", text: `Ansiedad: ${profile.f5Results.ansiedad.score}/15 — Moderada. Monitoreo con Oficial de Salud (IHO).` });

    const s = profile.f5Results.estres?.categoria;
    if (s === "critical")
      alerts.push({ variant: "danger", level: 2, type: "estres", text: `Estrés: ${profile.f5Results.estres.score}/20 — Crítico. Sugerencia de consulta con Médico General.` });
    else if (s === "high")
      alerts.push({ variant: "danger", level: 1, type: "estres", text: `Estrés: ${profile.f5Results.estres.score}/20 — Alto. Monitoreo con Oficial de Salud (IHO).` });

    // ── Stress Index (FaceHeart Baevsky scale: 0-1000+) ──────────────────
    // <50 Low · 50-200 Normal · 200-350 Mild · 350-500 High · ≥500 Very High
    const si = parseFloat(profile.stressIndex);
    if (!isNaN(si)) {
      if (si >= (t.stressIndex?.danger ?? 500))
        alerts.push({ variant: "danger", level: 2, type: "stressIndex", value: si, threshold: t.stressIndex?.danger, text: `Índice de estrés: ${si} — Muy alto. Sugerencia de consulta con Médico General.` });
      else if (si >= (t.stressIndex?.warning ?? 350))
        alerts.push({ variant: "warning", level: 1, type: "stressIndex", value: si, text: `Índice de estrés: ${si} — Alto. Monitoreo con Oficial de Salud (IHO).` });
      else if (si >= (t.stressIndex?.info ?? 200))
        alerts.push({ variant: "info", level: 0, type: "stressIndex", value: si, text: `Índice de estrés: ${si} — Moderado.` });
    }
  }


  // ── Reglas OMS ───────────────────────────────────────────────────────────
  if (omsConfig && omsConfig.rules) {
    const matchingRules = [];
    const r = omsConfig.rules;

    // 1. Edad
    if (r.age?.enabled) {
      const age = profile.fechaNacimiento ? calculateAge(profile.fechaNacimiento) : null;
      if (age !== null && age >= r.age.value) {
        matchingRules.push({ label: `Edad (${age} ≥ ${r.age.value} años)`, key: "age" });
      }
    }

    // 2. PAS (systolic)
    const pas = parseFloat(profile.paSistolica);
    if (r.systolic?.enabled && !isNaN(pas) && pas >= r.systolic.value) {
      matchingRules.push({ label: `PAS (${pas} ≥ ${r.systolic.value} mmHg)`, key: "systolic" });
    }

    // 3. PAD (diastolic)
    const pad = parseFloat(profile.paDiastolica);
    if (r.diastolic?.enabled && !isNaN(pad) && pad >= r.diastolic.value) {
      matchingRules.push({ label: `PAD (${pad} ≥ ${r.diastolic.value} mmHg)`, key: "diastolic" });
    }

    // 4. Smoking
    const isSmoker = profile.tabaco === "Si" || profile.tabaco === "Sí" || profile.smoking === true;
    if (r.smoking?.enabled && isSmoker) {
      matchingRules.push({ label: "Tabaquismo Activo", key: "smoking" });
    }

    // 5. Diabetes
    const glu = parseFloat(profile.glucemia);
    const hasDiabetesCond = profile.condicionIds?.some(id => ["cond-002", "cond-019"].includes(id));
    const isDiabetic = (glu >= 126) || profile.diabetes === true || profile.diabetes === "Si" || profile.diabetes === "Sí" || hasDiabetesCond;
    if (r.diabetes?.enabled && isDiabetic) {
      matchingRules.push({ label: "Diagnóstico de Diabetes", key: "diabetes" });
    }

    // 6. IMC
    const imc = calculateIMC(profile.peso, profile.talla);
    if (r.imc?.enabled && imc > 0 && imc >= r.imc.value) {
      matchingRules.push({ label: `IMC (${imc} ≥ ${r.imc.value} kg/m²)`, key: "imc" });
    }

    const minCond = omsConfig.triggerLogic?.minConditions ?? 2;
    if (matchingRules.length >= minCond) {
      const rulesStr = matchingRules.map(m => m.label).join(", ");
      const severity = omsConfig.triggerLogic?.alertSeverity || "warning";
      
      alerts.push({
        variant: severity,
        type: "oms",
        value: matchingRules.length,
        threshold: minCond,
        text: omsConfig.triggerLogic?.userActionTemplate || `Monitoreo OMS activado. Se han detectado ${matchingRules.length} factores de riesgo: ${rulesStr}.`,
        doctorText: omsConfig.triggerLogic?.doctorMessageTemplate || `Criterios de monitoreo cardiovascular de la OMS cumplidos: ${rulesStr}.`
      });
    }
  }

  // critical primero, luego danger, warning, info
  const order = { critical: 0, danger: 1, warning: 2, info: 3 };
  alerts.sort((a, b) => order[a.variant] - order[b.variant]);
  return alerts;
}

// ── Label mappers ────────────────────────────────────────
function extractLabelKey(val) {
  if (val && typeof val === "object") {
    return val.key || val.displayES || val.display || "";
  }
  return val;
}

export function ipaqLabel(key) {
  const k = extractLabelKey(key);
  return { high: "Alto", moderate: "Moderado", low: "Bajo" }[k] || k;
}
export function dietCatLabel(key) {
  const k = extractLabelKey(key);
  return { poor: "Deficiente", fair: "Mejorable", good: "Buena", excellent: "Excelente" }[k] || k;
}
export function behaviorCatLabel(key) {
  const k = extractLabelKey(key);
  return { risk: "De riesgo", fair: "Regulares", good: "Saludables", optimal: "Óptimas" }[k] || k;
}
export function hydrationLabel(key) {
  const k = extractLabelKey(key);
  return { insufficient: "Insuficiente", low: "Bajo", adequate: "Adecuado", optimal: "Óptimo" }[k] || k;
}
export function sedLabel(key) {
  const k = extractLabelKey(key);
  return { low: "Bajo", moderate: "Moderado", high: "Alto", very_high: "Muy alto" }[k] || k;
}
export function emotionalLabel(key) {
  const k = extractLabelKey(key);
  return { critical: "Crítico", low: "Bajo", moderate: "Moderado", good: "Bueno", excellent: "Excelente" }[k] || k;
}
export function anxietyLabel(key) {
  const k = extractLabelKey(key);
  return { minimal: "Mínima", mild: "Leve", moderate: "Moderada", severe: "Severa" }[k] || k;
}
export function stressLabel(key) {
  const k = extractLabelKey(key);
  return { low: "Bajo", moderate: "Moderado", high: "Alto", critical: "Crítico" }[k] || k;
}
export function wellbeingLabel(key) {
  const k = extractLabelKey(key);
  return { low: "Bajo", moderate: "Moderado", high: "Alto", very_high: "Muy alto" }[k] || k;
}

// ── Semantic color ────────────────────────────────────────
export function semanticColor(category) {
  const green = ["Normal", "Buena", "Bueno", "Excelente", "Óptimo", "Óptimas", "Adecuado", "Saludables", "Mínima", "Bajo", "Alto", "Muy alto", "Apto"];
  const yellow = ["Elevada", "Moderado", "Moderada", "Mejorable", "Regulares", "Leve", "Sobrepeso", "Bajo peso", "Hiperglicemia"];
  const red = ["HTA II", "HTA I", "Diabetes", "Obesidad", "Deficiente", "De riesgo", "Crítico", "Severa", "Insuficiente"];
  if (red.includes(category)) return "#C0453A";
  if (yellow.includes(category)) return "#F9A01B";
  if (green.includes(category)) return "#157346";
  return "#B5A998";
}
